import dynamic from 'next/dynamic';
import Image from 'next/image';
import type {
  ComponentsAutoplayVideoItem,
  ComponentsAutoplayVideoItemStyle,
} from 'queries/graphql';

import { buildImgProps } from 'utils/media';
import { useTextFontOverrides, useHeadingFontOverrides } from 'utils/font-override';
import { buildCssVar } from 'utils/style-override';

import LazyAutoplayVideo from 'generic/LazyAutoplayVideo';
import Bullet from 'generic/Bullet';

const MarkdownText = dynamic(() => import('generic/MarkdownText'));

import styles from './styles.module.scss';

export default function AutoplayVideoItem({
  placeholderImage,
  video,
  image,
  tagline,
  heading,
  text,
  bullets,
  itemStyle,
}: ComponentsAutoplayVideoItem & {
  itemStyle?: ComponentsAutoplayVideoItemStyle;
}) {
  const { headingColor, textColor, taglineColor, taglineBackgroundColor, headingFont, textFont } =
    itemStyle || {};

  const headingFontOverrides = useHeadingFontOverrides(headingFont);
  const textFontOverrides = useTextFontOverrides(textFont);

  return (
    <div className={`root ${styles.box_item}`}>
      <style jsx>{`
        .root {
          ${buildCssVar('--heading-color', headingColor, 'var(--heading-color-dark)')}
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${buildCssVar('--tagline-color', taglineColor, 'var(--text-color-dark)')}
          ${buildCssVar(
            '--tagline-background-color',
            taglineBackgroundColor,
            'var(--default-background-color)'
          )}
          ${headingFontOverrides ?? ''}
          ${textFontOverrides ?? ''}
        }
      `}</style>

      <div className={styles.media_container}>
        {video && (
          <div className={styles.video_container}>
            <LazyAutoplayVideo placeholderImage={placeholderImage} video={video} />
          </div>
        )}
        {image && (
          <div className={styles.image_wrapper}>
            <Image {...buildImgProps(image)} alt={image.alt} />
          </div>
        )}
      </div>
      {tagline && (
        <div className={`${styles.tagline} ${taglineBackgroundColor ? styles.fancy_tagline : ''}`}>
          {tagline}
        </div>
      )}
      {heading && <h3 className={styles.heading}>{heading}</h3>}
      {text && <div className={styles.text}>{text && <MarkdownText text={text} />}</div>}
      <div className={styles.bullet_container}>
        {bullets.map(({ bullet }) => (
          <Bullet key={bullet.id} {...bullet} />
        ))}
      </div>
    </div>
  );
}
